'use client';

import Link from 'next/link';
import { ClipboardList, ChevronRight } from 'lucide-react';
import { formatDate } from '@paeffgen/react/crm-documents/util';
import type { MaterialRequest } from '@paeffgen/react/crm-documents/data-access-graphql';

/**
 * Baut die Positions-Vorschau einer Verlaufskarte: bis zu `max`
 * Beschreibungen, der Rest als „+N weitere". Leere Beschreibungen werden
 * weder gezeigt noch gezählt.
 */
export function buildItemPreview(
  items: Array<{ description: string }>,
  max = 3,
): string {
  const names = items.map((i) => i.description).filter(Boolean);
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  const base = shown.join(' · ');
  return rest > 0 ? `${base} · +${rest} weitere` : base;
}

interface RequestHistoryCardProps {
  request: MaterialRequest;
  onReorder: (request: MaterialRequest) => void;
}

export function RequestHistoryCard({ request, onReorder }: RequestHistoryCardProps) {
  const count = request.items.length;
  const preview = buildItemPreview(request.items, 3);

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-5 shadow-[0_2px_4px_rgba(44,44,43,0.05)]">
      <div className="min-w-0">
        <h3 className="font-bold text-secondary truncate">
          {request.commission || 'Anfrage ohne Bezeichnung'}
        </h3>
        <p className="text-sm text-zinc-500 mt-0.5 tabular-nums">
          {formatDate(request.updatedAt, 'dd.MM.yyyy')} · {count}{' '}
          {count === 1 ? 'Position' : 'Positionen'}
          {request.navDocumentNo ? ` · ${request.navDocumentNo}` : ''}
        </p>
      </div>

      {preview && (
        <p className="text-sm text-zinc-600 mt-3 border-t border-zinc-100 pt-3">
          {preview}
        </p>
      )}

      <div className="flex items-center gap-2 mt-4">
        <button
          type="button"
          onClick={() => onReorder(request)}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-50 text-primary rounded-lg font-bold text-sm hover:bg-orange-100 transition-colors"
        >
          <ClipboardList size={16} />
          Erneut anfragen
        </button>
        <Link
          href={`/materialanfrage/${request.id}`}
          aria-label="Anfrage ansehen"
          className="inline-flex items-center justify-center w-11 h-11 border border-zinc-300 rounded-lg text-zinc-500 hover:bg-zinc-50 transition-colors shrink-0"
        >
          <ChevronRight size={18} />
        </Link>
      </div>
    </div>
  );
}
